import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { getRequests, updateRequestStatus } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, Package, MapPin, Clock, CheckCircle, XCircle,
  ArrowRight, Inbox, Send, AlertTriangle, Truck, Euro, DollarSign, Tag
} from 'lucide-react';
import { cn, fmt2 } from '@/lib/utils';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  awaiting_logistics_confirmation: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
};

const STATUS_LABELS = {
  pending: 'Ausstehend',
  approved: 'Genehmigt',
  awaiting_logistics_confirmation: 'Logistik-Bestatigung',
  rejected: 'Abgelehnt',
  completed: 'Abgeschlossen'
};

export const RequestsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [myRequests, setMyRequests] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialTab = searchParams.get('tab') === 'incoming' ? 'incoming' : 'my';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null, request: null });
  const [logisticsCost, setLogisticsCost] = useState('');

  // Sync tab → URL
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'incoming') {
      setSearchParams({ tab: 'incoming' }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  // React to URL changes (e.g., menu badge click navigates back to /requests?tab=incoming)
  useEffect(() => {
    const urlTab = searchParams.get('tab') === 'incoming' ? 'incoming' : 'my';
    if (urlTab !== activeTab) setActiveTab(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (user) loadAllRequests();
  }, [user]);

  const loadAllRequests = async () => {
    setLoading(true);
    try {
      const [myRes, allRes] = await Promise.all([
        getRequests({ my_requests: true }),
        getRequests({})
      ]);
      setMyRequests(myRes.data);
      setIncomingRequests(allRes.data.filter(r => r.requester_user !== user.user_id));
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (requestId, status, extra = {}) => {
    try {
      await updateRequestStatus(requestId, { status, ...extra });
      toast.success(status === 'approved' ? 'Anfrage genehmigt' : status === 'rejected' ? 'Anfrage abgelehnt' : 'Anfrage abgeschlossen');
      loadAllRequests();
      setActionDialog({ open: false, action: null, request: null });
      setSelectedRequest(null);
      setLogisticsCost('');
    } catch (error) {
      console.error('Failed to update request:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <FileText className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h1 className="text-2xl font-bold mb-2">{t('requests.loginRequired')}</h1>
          <p className="text-muted-foreground mb-6">{t('requests.loginHint')}</p>
          <Button asChild className="bg-orange-500 hover:bg-orange-600">
            <Link to="/login">{t('nav.login')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Separate my requests that need logistics confirmation from me
  const myAwaitingLogistics = myRequests.filter(r => r.status === 'awaiting_logistics_confirmation');
  const myOtherRequests = myRequests.filter(r => r.status !== 'awaiting_logistics_confirmation');

  return (
    <div className="min-h-screen bg-background" data-testid="requests-page">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-manrope text-3xl font-bold mb-8">{t('nav.requests')}</h1>

        {/* Alert banner for logistics confirmations */}
        {myAwaitingLogistics.length > 0 && (
          <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg" data-testid="logistics-alert">
            <div className="flex items-center gap-2 mb-2">
              <Truck className="h-5 w-5 text-purple-600" />
              <span className="font-semibold text-purple-800 dark:text-purple-300">
                {myAwaitingLogistics.length} Anfrage(n) warten auf Ihre Logistik-Bestatigung
              </span>
            </div>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              Bitte bestatigen oder lehnen Sie die Logistikkosten ab.
            </p>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6">
            <TabsTrigger value="my" data-testid="tab-my-requests" className="gap-2">
              <Send className="h-4 w-4" />
              {t('requests.tab.my')}
              {myRequests.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-xs">{myRequests.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="incoming" data-testid="tab-incoming-requests" className="gap-2">
              <Inbox className="h-4 w-4" />
              {t('requests.tab.incoming')}
              {incomingRequests.filter(r => r.status === 'pending').length > 0 && (
                <Badge className="ml-1 h-5 min-w-[20px] px-1.5 text-xs bg-orange-500">
                  {incomingRequests.filter(r => r.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my">
            <RequestsList
              requests={myRequests}
              loading={loading}
              onSelect={setSelectedRequest}
              showActions={false}
              isMyRequests={true}
              user={user}
              onConfirmLogistics={(req) => {
                setActionDialog({ open: true, action: 'confirm_logistics', request: req });
              }}
              onRejectLogistics={(req) => {
                setActionDialog({ open: true, action: 'reject_logistics', request: req });
              }}
              emptyIcon={<Send className="h-16 w-16 mx-auto text-slate-300 mb-4" />}
              emptyTitle={t('requests.empty.my')}
              emptyText={t('requests.empty.myHint')}
            />
          </TabsContent>

          <TabsContent value="incoming">
            <RequestsList
              requests={incomingRequests}
              loading={loading}
              onSelect={setSelectedRequest}
              showActions={true}
              isMyRequests={false}
              user={user}
              onApprove={(req) => {
                setSelectedRequest(req);
                setLogisticsCost('');
                setActionDialog({ open: true, action: 'approve', request: req });
              }}
              onReject={(req) => {
                setSelectedRequest(req);
                setActionDialog({ open: true, action: 'reject', request: req });
              }}
              onComplete={(req) => {
                setSelectedRequest(req);
                setActionDialog({ open: true, action: 'complete', request: req });
              }}
              emptyIcon={<Inbox className="h-16 w-16 mx-auto text-slate-300 mb-4" />}
              emptyTitle={t('requests.empty.incoming')}
              emptyText={t('requests.empty.incomingHint')}
            />
          </TabsContent>
        </Tabs>

        {/* Request Detail Dialog */}
        <Dialog open={!!selectedRequest && !actionDialog.open} onOpenChange={(open) => !open && setSelectedRequest(null)}>
          <DialogContent className="max-w-lg" data-testid="request-detail-dialog">
            <DialogHeader>
              <DialogTitle>Anfrage-Details</DialogTitle>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <h4 className="font-semibold mb-2">{selectedRequest.material?.common_title}</h4>
                  <p className="text-sm text-muted-foreground">
                    Rollennummer / Lagerplatz: {selectedRequest.sku?.article_number}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Menge</p>
                    <p className="font-semibold">{selectedRequest.requested_qty} {selectedRequest.sku?.uom}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Typ</p>
                    <Badge variant="outline">
                      {selectedRequest.request_type === 'internal_transfer' ? 'Intern' : 'Extern'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Systempreis</p>
                    <p className="font-semibold text-orange-500">
                      {selectedRequest.system_price_EUR ? `${fmt2(selectedRequest.system_price_EUR)} EUR` : 'Auf Anfrage'}
                    </p>
                  </div>
                  {selectedRequest.bid_price_EUR && (
                    <div>
                      <p className="text-sm text-muted-foreground">Verkaufspreis</p>
                      <p className="font-semibold text-orange-500">{fmt2(selectedRequest.bid_price_EUR)} EUR</p>
                    </div>
                  )}
                  {selectedRequest.book_value_EUR != null && (
                    <div>
                      <p className="text-sm text-muted-foreground">Verkaufspreis (System)</p>
                      <p className="font-semibold text-green-500">{fmt2(selectedRequest.book_value_EUR)} EUR</p>
                    </div>
                  )}
                  {selectedRequest.incoterm && (
                    <div>
                      <p className="text-sm text-muted-foreground">Incoterm</p>
                      <p className="font-semibold">{selectedRequest.incoterm}</p>
                    </div>
                  )}
                  {selectedRequest.delivery_site && (
                    <div>
                      <p className="text-sm text-muted-foreground">Lieferstandort</p>
                      <p className="font-semibold">{selectedRequest.delivery_site.site_name}</p>
                    </div>
                  )}
                  {selectedRequest.logistics_cost_EUR != null && (
                    <div>
                      <p className="text-sm text-muted-foreground">Logistikkosten</p>
                      <p className="font-semibold text-purple-600">{fmt2(selectedRequest.logistics_cost_EUR)} EUR</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Angefragt von</p>
                    <p className="font-semibold">{selectedRequest.requester?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Datum</p>
                    <p className="font-semibold">{new Date(selectedRequest.requested_at).toLocaleDateString('de-DE')}</p>
                  </div>
                </div>

                {selectedRequest.message && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Nachricht</p>
                    <p className="text-sm p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">{selectedRequest.message}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Action Confirmation Dialog */}
        <Dialog open={actionDialog.open} onOpenChange={(open) => { if (!open) { setActionDialog({ open: false, action: null, request: null }); setLogisticsCost(''); } }}>
          <DialogContent data-testid="request-action-dialog">
            <DialogHeader>
              <DialogTitle>
                {actionDialog.action === 'approve' && 'Anfrage genehmigen'}
                {actionDialog.action === 'reject' && 'Anfrage ablehnen'}
                {actionDialog.action === 'complete' && 'Anfrage abschliessen'}
                {actionDialog.action === 'confirm_logistics' && 'Logistikkosten bestatigen'}
                {actionDialog.action === 'reject_logistics' && 'Logistikkosten ablehnen'}
              </DialogTitle>
              <DialogDescription>
                {actionDialog.action === 'approve' && 'Mochten Sie diese Anfrage genehmigen? Optional konnen Sie Logistikkosten angeben.'}
                {actionDialog.action === 'reject' && 'Mochten Sie diese Anfrage ablehnen?'}
                {actionDialog.action === 'complete' && 'Mochten Sie diese Anfrage als abgeschlossen markieren?'}
                {actionDialog.action === 'confirm_logistics' && `Logistikkosten: ${fmt2(actionDialog.request?.logistics_cost_EUR)} EUR. Möchten Sie diese Kosten akzeptieren und die Anfrage abschliessen?`}
                {actionDialog.action === 'reject_logistics' && 'Mochten Sie die Logistikkosten ablehnen? Die Anfrage wird abgelehnt.'}
              </DialogDescription>
            </DialogHeader>

            {actionDialog.action === 'approve' && (
              <div className="py-2 space-y-3">
                {actionDialog.request?.system_price_EUR && (
                  <div className="flex items-center gap-2 text-sm">
                    <Euro className="h-4 w-4 text-orange-500" />
                    <span>Systempreis: <strong>{fmt2(actionDialog.request.system_price_EUR)} EUR</strong></span>
                  </div>
                )}
                {actionDialog.request?.bid_price_EUR && (
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="h-4 w-4 text-orange-500" />
                    <span>Verkaufspreis: <strong>{fmt2(actionDialog.request.bid_price_EUR)} EUR</strong></span>
                  </div>
                )}
                {actionDialog.request?.book_value_EUR != null && (
                  <div className="flex items-center gap-2 text-sm">
                    <Euro className="h-4 w-4 text-green-500" />
                    <span>Verkaufspreis (System): <strong>{fmt2(actionDialog.request.book_value_EUR)} EUR</strong></span>
                  </div>
                )}
                <Separator />
                <div>
                  <Label>Logistikkosten (EUR) - optional</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={logisticsCost}
                    onChange={(e) => setLogisticsCost(e.target.value)}
                    placeholder="0.00"
                    data-testid="logistics-cost-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Falls Logistikkosten anfallen, muss der Anfrager diese bestatigen.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => { setActionDialog({ open: false, action: null, request: null }); setLogisticsCost(''); }} data-testid="action-cancel-btn">
                Abbrechen
              </Button>
              <Button
                data-testid="action-confirm-btn"
                className={cn(
                  (actionDialog.action === 'reject' || actionDialog.action === 'reject_logistics') && 'bg-red-500 hover:bg-red-600',
                  actionDialog.action === 'approve' && 'bg-green-500 hover:bg-green-600',
                  actionDialog.action === 'complete' && 'bg-blue-500 hover:bg-blue-600',
                  actionDialog.action === 'confirm_logistics' && 'bg-green-500 hover:bg-green-600'
                )}
                onClick={() => {
                  const req = actionDialog.request || selectedRequest;
                  if (!req) return;
                  if (actionDialog.action === 'approve') {
                    const extra = logisticsCost ? { logistics_cost_EUR: parseFloat(logisticsCost) } : {};
                    handleStatusUpdate(req.request_id, 'approved', extra);
                  } else if (actionDialog.action === 'reject') {
                    handleStatusUpdate(req.request_id, 'rejected');
                  } else if (actionDialog.action === 'complete') {
                    handleStatusUpdate(req.request_id, 'completed');
                  } else if (actionDialog.action === 'confirm_logistics') {
                    handleStatusUpdate(req.request_id, 'completed');
                  } else if (actionDialog.action === 'reject_logistics') {
                    handleStatusUpdate(req.request_id, 'rejected');
                  }
                }}
              >
                {actionDialog.action === 'approve' && 'Genehmigen'}
                {actionDialog.action === 'reject' && 'Ablehnen'}
                {actionDialog.action === 'complete' && 'Abschliessen'}
                {actionDialog.action === 'confirm_logistics' && 'Bestatigen & Abschliessen'}
                {actionDialog.action === 'reject_logistics' && 'Ablehnen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

const RequestsList = ({
  requests, loading, onSelect, showActions, isMyRequests, user,
  onApprove, onReject, onComplete, onConfirmLogistics, onRejectLogistics,
  emptyIcon, emptyTitle, emptyText
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-16" data-testid="requests-empty">
        {emptyIcon}
        <h2 className="text-xl font-semibold mb-2">{emptyTitle}</h2>
        <p className="text-muted-foreground">{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="requests-list">
      {requests.map((request) => (
        <Card key={request.request_id} className="overflow-hidden" data-testid={`request-card-${request.request_id}`}>
          <CardContent className="p-0">
            <div className="flex flex-col md:flex-row">
              {/* Material Info */}
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between mb-2">
                  <Link
                    to={`/material/${request.material?.material_id}`}
                    className="font-semibold hover:text-orange-500 transition-colors"
                    data-testid="request-material-link"
                  >
                    {request.material?.common_title}
                  </Link>
                  <div className="flex gap-1 ml-2 shrink-0">
                    <Badge className={cn(STATUS_COLORS[request.status])} data-testid="request-status-badge">
                      {STATUS_LABELS[request.status] || t(`status.${request.status}`) || request.status}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {request.request_type === 'internal_transfer' ? 'Intern' : 'Extern'}
                    </Badge>
                    {request.is_escalated && (
                      <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" data-testid="escalated-badge">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Eskaliert
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Package className="h-4 w-4" />
                    {request.requested_qty} {request.sku?.uom}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {request.site?.site_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {new Date(request.requested_at).toLocaleDateString('de-DE')}
                  </span>
                  {request.system_price_EUR && (
                    <span className="flex items-center gap-1 text-orange-500 font-medium">
                      <Euro className="h-4 w-4" />
                      {fmt2(request.system_price_EUR)} EUR
                    </span>
                  )}
                  {request.bid_price_EUR && (
                    <span className="flex items-center gap-1 text-orange-500 font-medium">
                      <Tag className="h-4 w-4" />
                      VP: {fmt2(request.bid_price_EUR)} EUR
                    </span>
                  )}
                  {request.book_value_EUR != null && request.book_value_EUR !== request.bid_price_EUR && (
                    <span className="flex items-center gap-1 text-green-500 font-medium">
                      VP (System): {fmt2(request.book_value_EUR)} EUR
                    </span>
                  )}
                  {request.incoterm && (
                    <span className="flex items-center gap-1">
                      <Truck className="h-4 w-4" />
                      {request.incoterm}
                    </span>
                  )}
                </div>

                {request.delivery_site && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Lieferung an: <span className="font-medium text-foreground">{request.delivery_site.site_name}</span>
                  </p>
                )}

                {request.logistics_cost_EUR != null && (
                  <p className="text-sm mt-1">
                    <span className="text-purple-600 font-medium">
                      Logistikkosten: {fmt2(request.logistics_cost_EUR)} EUR
                    </span>
                    {request.logistics_confirmed === true && <span className="text-green-600 ml-2">(Bestatigt)</span>}
                    {request.logistics_confirmed === false && <span className="text-red-600 ml-2">(Abgelehnt)</span>}
                  </p>
                )}

                {showActions && request.requester && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Von: <span className="font-medium text-foreground">{request.requester.name}</span>
                  </p>
                )}

                {request.message && (
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2 italic">"{request.message}"</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 p-4 bg-slate-50 dark:bg-slate-800/50 md:flex-col md:justify-center">
                <Button variant="outline" size="sm" onClick={() => onSelect(request)} data-testid="view-request-btn">
                  Details
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>

                {/* Incoming: Approve/Reject pending requests */}
                {showActions && request.status === 'pending' && (
                  <div className="flex gap-2 md:flex-col">
                    <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => onApprove(request)} data-testid="approve-request-btn">
                      Genehmigen
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => onReject(request)} data-testid="reject-request-btn">
                      Ablehnen
                    </Button>
                  </div>
                )}

                {/* Incoming: Complete approved requests */}
                {showActions && request.status === 'approved' && (
                  <Button size="sm" className="bg-blue-500 hover:bg-blue-600" onClick={() => onComplete(request)} data-testid="complete-request-btn">
                    Abschliessen
                  </Button>
                )}

                {/* My requests: Confirm/Reject logistics */}
                {isMyRequests && request.status === 'awaiting_logistics_confirmation' && (
                  <div className="flex gap-2 md:flex-col">
                    <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => onConfirmLogistics(request)} data-testid="confirm-logistics-btn">
                      Bestatigen
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => onRejectLogistics(request)} data-testid="reject-logistics-btn">
                      Ablehnen
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
